import { DecorativeList } from "@genuin/ui/components/decorative-list";
import { CommunityPrivacyInfo } from "@genuin/components/molecules/community-privacy-info";
import { GroupPrivacyInfo } from "@genuin/components/molecules/group-privacy-info";
import { Avatar } from "@genuin/ui/components/avatar";
import { RepostCommunityType } from "@genuin/components/react-query/api/repost/schema";

import { Button } from "@genuin/ui/button";
import { RepostIcon } from "@genuin/ui/icons";
import { useRepostVideoMutation } from "@genuin/components/react-query/api/repost";
import { useCallback } from "react";
import { Toast } from "@genuin/ui/components/toaster";
import { cn } from "@genuin/ui/lib/utils";
import { Link } from "@genuin/components/molecules/link";
import { buildPageUrl } from "@genuin/components/lib/utils/pages";
import { useAnalytics } from "@genuin/components/context";

export function CommunityCard({
  communityInfo,
  videoId,
}: {
  communityInfo: RepostCommunityType;
  videoId: string;
}) {
  return (
    <div>
      <div className="gencl:flex gencl:gap-x-3">
        <Avatar
          size="md"
          alt={communityInfo.name ?? ""}
          imageUrl={communityInfo.dp_m ?? communityInfo.dp ?? ""}
          isAvatar={false}
        />
        <div>
          <span className="gencl:space-y-1">
            <Link
              href={buildPageUrl({
                type: "community",
                slug: communityInfo.slug,
              })}
            >
              <p
                title={communityInfo.name ?? ""}
                className="gencl:line-clamp-2 gencl:text-body-1-bold"
              >
                {communityInfo.name}
              </p>
            </Link>
            <CommunityPrivacyInfo
              isPrivate={communityInfo.type === "PRIVATE"}
              className="gencl:text-body-2-medium gencl:text-secondary-600!"
            />
          </span>
        </div>
      </div>
      <DecorativeList className="gencl:ml-4 gencl:space-y-2">
        <div className="gencl:h-0.5" />
        {communityInfo.chats.map((item, index) => {
          return (
            <li key={index}>
              <div className="gencl:w-full gencl:flex gencl:justify-between gencl:rounded-lg gencl:border gencl:border-secondary-150 gencl:bg-secondary-50 gencl:px-4 gencl:py-3">
                <div className="gencl:space-y-1">
                  <Link href={buildPageUrl({ type: "group", slug: item.slug })}>
                    <p className="gencl:line-clamp-1 gencl:break-all gencl:text-body-1-semi-bold">
                      {item.group.group_name}
                    </p>
                  </Link>
                  {item.actions[0] && (
                    <GroupPrivacyInfo
                      accessTypeId={item.actions[0]?.access_type_id}
                      actionId={item.actions[0]?.action_id}
                    />
                  )}
                </div>
                <RepostButton videoId={videoId} destinationId={item.chat_id} />
              </div>
            </li>
          );
        })}
      </DecorativeList>
    </div>
  );
}

function RepostButton({
  destinationId,
  videoId,
}: {
  destinationId: string;
  videoId: string;
}) {
  const { track, EventName } = useAnalytics();

  const {
    mutate: repostVideo,
    isPending,
    isSuccess,
  } = useRepostVideoMutation({
    onSuccess: () => {
      track(EventName.VIDEO_REPOST, {
        content_id: videoId,
        content_category: "loop",
        event_record_screen: "feed",
        event_target_screen: "none",
      });
      Toast.Success({ message: "Video reposted successfully!" });
    },
    onError: () => {
      Toast.Error({
        message: "Failed to repost video. Please try again later.",
      });
    },
  });

  const handleRepost = useCallback(() => {
    repostVideo({ destinationId, sourceVideoId: videoId });
  }, [destinationId, videoId]);

  return (
    <Button
      theme={isSuccess ? "primary" : "outline"}
      disabled={isPending || isSuccess}
      onClick={handleRepost}
    >
      <RepostIcon
        className={cn("gencl:size-6", isPending && "gencl:animate-spin")}
      />
      {isSuccess ? "Reposted" : "Repost"}
    </Button>
  );
}
